import { REGISTRATION_REDIRECT_STORAGE_KEY } from '../constants/storageKeyConstants';

export const RegistrationRedirectPhase = {
  REDIRECTING: 'redirecting',
  MSAL_READY: 'msal_ready',
} as const;

export type RegistrationRedirectPhaseType =
  (typeof RegistrationRedirectPhase)[keyof typeof RegistrationRedirectPhase];

export type RegistrationAuthFlow = 'existing' | 'custom';

/** Persisted in sessionStorage — never includes access tokens. */
export interface RegistrationRedirectSessionState {
  phase: RegistrationRedirectPhaseType;
  flow: RegistrationAuthFlow;
  /** Provider contact email from demographics. */
  contactEmail?: string;
}

export type RegistrationRedirectReadyState = RegistrationRedirectSessionState & {
  phase: typeof RegistrationRedirectPhase.MSAL_READY;
  accessToken: string;
};

/** In-memory only — survives SPA navigation, not full page reload. */
let registrationAccessTokenHandoff: string | null = null;
const readyListeners = new Set<() => void>();

function isRegistrationAuthFlow(flow: unknown): flow is RegistrationAuthFlow {
  return flow === 'existing' || flow === 'custom';
}

function isRegistrationRedirectPhase(phase: unknown): phase is RegistrationRedirectPhaseType {
  return phase === RegistrationRedirectPhase.REDIRECTING || phase === RegistrationRedirectPhase.MSAL_READY;
}

function parseRegistrationRedirectSessionState(raw: string): RegistrationRedirectSessionState | null {
  try {
    const parsed = JSON.parse(raw) as RegistrationRedirectSessionState & { accessToken?: string };
    if (!isRegistrationRedirectPhase(parsed?.phase) || !isRegistrationAuthFlow(parsed?.flow)) {
      return null;
    }

    return {
      phase: parsed.phase,
      flow: parsed.flow,
      contactEmail: parsed.contactEmail,
    };
  } catch {
    return null;
  }
}

function setRegistrationAccessTokenHandoff(accessToken: string): void {
  registrationAccessTokenHandoff = accessToken;
}

function takeRegistrationAccessTokenHandoff(): string | null {
  const token = registrationAccessTokenHandoff;
  registrationAccessTokenHandoff = null;
  return token;
}

function clearRegistrationAccessTokenHandoff(): void {
  registrationAccessTokenHandoff = null;
}

function notifyRegistrationRedirectReady(): void {
  readyListeners.forEach((listener) => {
    listener();
  });
}

export function subscribeRegistrationRedirectReady(listener: () => void): () => void {
  readyListeners.add(listener);
  return () => {
    readyListeners.delete(listener);
  };
}

export function setRegistrationRedirect(state: RegistrationRedirectSessionState): void {
  sessionStorage.setItem(REGISTRATION_REDIRECT_STORAGE_KEY, JSON.stringify(state));
}

export function getRegistrationRedirect(): RegistrationRedirectSessionState | null {
  const raw = sessionStorage.getItem(REGISTRATION_REDIRECT_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  const parsed = parseRegistrationRedirectSessionState(raw);
  if (!parsed) {
    clearRegistrationRedirect();
    return null;
  }

  return parsed;
}

export function beginRegistrationRedirect(state: {
  flow: RegistrationAuthFlow;
  contactEmail: string;
}): void {
  clearRegistrationAccessTokenHandoff();
  setRegistrationRedirect({
    phase: RegistrationRedirectPhase.REDIRECTING,
    flow: state.flow,
    contactEmail: state.contactEmail,
  });
}

function commitRegistrationRedirectMsalReady(
  sessionState: RegistrationRedirectSessionState,
  accessToken: string,
): RegistrationRedirectReadyState {
  setRegistrationRedirect({
    ...sessionState,
    phase: RegistrationRedirectPhase.MSAL_READY,
  });
  setRegistrationAccessTokenHandoff(accessToken);
  notifyRegistrationRedirectReady();

  return {
    ...sessionState,
    phase: RegistrationRedirectPhase.MSAL_READY,
    accessToken,
  };
}

/** Promotes in-flight registration redirect to MSAL-ready; token kept in memory only. */
export function markRegistrationRedirectMsalReady(accessToken: string): RegistrationRedirectReadyState | null {
  const current = getRegistrationRedirect();
  if (!current || current.phase !== RegistrationRedirectPhase.REDIRECTING) {
    return null;
  }

  return commitRegistrationRedirectMsalReady(current, accessToken);
}

/** Fallback when redirecting session state is missing but handler has a registration token. */
export function setRegistrationRedirectMsalReadyHandoff(state: {
  accessToken: string;
  flow: RegistrationAuthFlow;
  contactEmail?: string;
}): RegistrationRedirectReadyState {
  return commitRegistrationRedirectMsalReady(
    {
      phase: RegistrationRedirectPhase.MSAL_READY,
      flow: state.flow,
      contactEmail: state.contactEmail,
    },
    state.accessToken,
  );
}

export function isRegistrationRedirectInFlight(): boolean {
  return getRegistrationRedirect()?.phase === RegistrationRedirectPhase.REDIRECTING;
}

export function isRegistrationRedirectActive(): boolean {
  return getRegistrationRedirect() != null;
}

/** Returns MSAL-ready registration state once, then clears session + in-memory token. */
export function consumeRegistrationRedirectReady(): RegistrationRedirectReadyState | null {
  const current = getRegistrationRedirect();
  if (!current || current.phase !== RegistrationRedirectPhase.MSAL_READY) {
    return null;
  }

  const accessToken = takeRegistrationAccessTokenHandoff();
  if (!accessToken) {
    clearRegistrationRedirect();
    return null;
  }

  clearRegistrationRedirect();
  return {
    ...current,
    phase: RegistrationRedirectPhase.MSAL_READY,
    accessToken,
  };
}

export function clearRegistrationRedirect(): void {
  sessionStorage.removeItem(REGISTRATION_REDIRECT_STORAGE_KEY);
  sessionStorage.removeItem('registration_auth_pending');
  sessionStorage.removeItem('registration_msal_ready');
  clearRegistrationAccessTokenHandoff();
}

/** @internal Test helper */
export function hasRegistrationAccessTokenHandoff(): boolean {
  return registrationAccessTokenHandoff != null;
}
